/**
 * Phase 7 (ADR-0012 optional): WAL-light für Sale-Finalize.
 * Hilft bei Diagnose/Replay wenn Sale-Write mittendrin fehlschlägt.
 */

import { kvGet, kvSetPersisted, listKvKeys } from './kvAccess';

const JOURNAL_KEY_PREFIX = 'storage_sale_finalize_journal_';
const MAX_JOURNAL_ENTRIES = 200;

function journalKey(businessId) {
  return `${JOURNAL_KEY_PREFIX}${String(businessId)}`;
}

function readJournal(businessId) {
  const raw = kvGet(journalKey(businessId));
  return Array.isArray(raw) ? raw : [];
}

function writeJournal(businessId, entries) {
  void kvSetPersisted(journalKey(businessId), entries.slice(-MAX_JOURNAL_ENTRIES));
}

export function appendSaleFinalizeJournalPending(businessId, payload = {}) {
  if (!businessId) return null;
  const now = new Date().toISOString();
  const id = `sfj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const entry = {
    id,
    status: 'pending',
    createdAt: now,
    updatedAt: now,
    orderId: payload.orderId || null,
    saleId: payload.saleId || null,
    clientMutationId: payload.clientMutationId || null,
    totalAmount: Number(payload.totalAmount || 0),
    // Snapshot für Repair/Replay (Phase 8).
    // Muss serialisierbar sein (KV speichert JSON).
    saleData: payload.saleData ?? null
  };
  const journal = readJournal(businessId);
  journal.push(entry);
  writeJournal(businessId, journal);
  return entry;
}

export function markSaleFinalizeJournalCommitted(businessId, journalId, sale = null) {
  if (!businessId || !journalId) return false;
  const journal = readJournal(businessId);
  const idx = journal.findIndex((j) => j.id === journalId);
  if (idx < 0) return false;
  journal[idx] = {
    ...journal[idx],
    status: 'committed',
    updatedAt: new Date().toISOString(),
    saleId: sale?.id || journal[idx].saleId,
    orderId: sale?.orderId || journal[idx].orderId
  };
  writeJournal(businessId, journal);
  return true;
}

export function markSaleFinalizeJournalFailed(businessId, journalId, errorMessage = '') {
  if (!businessId || !journalId) return false;
  const journal = readJournal(businessId);
  const idx = journal.findIndex((j) => j.id === journalId);
  if (idx < 0) return false;
  journal[idx] = {
    ...journal[idx],
    status: 'failed',
    updatedAt: new Date().toISOString(),
    error: String(errorMessage || 'unknown_error').slice(0, 500)
  };
  writeJournal(businessId, journal);
  return true;
}

export function listSaleFinalizeJournal(businessId, options = {}) {
  if (!businessId) return [];
  const { status = null, limit = 50 } = options;
  const journal = readJournal(businessId);
  const filtered = status ? journal.filter((j) => j.status === status) : journal;
  return filtered.slice(-Math.max(1, limit));
}

export function listSaleFinalizeJournalBusinessIds() {
  const out = new Set();
  try {
    const keys = listKvKeys();
    for (const key of keys) {
      if (!key || !key.startsWith(JOURNAL_KEY_PREFIX)) continue;
      const inner = key.slice(JOURNAL_KEY_PREFIX.length);
      if (inner) out.add(inner);
    }
  } catch {
    // ignore
  }
  return Array.from(out);
}

/**
 * Entfernt alte Journal-Einträge:
 * - committed älter als `committedTtlMs`
 * - failed älter als `failedTtlMs`
 * - pending älter als `pendingTtlMs` werden auf failed gesetzt
 */
export function sweepSaleFinalizeJournal(businessId, options = {}) {
  if (!businessId) {
    return { swept: false, reason: 'no_business_id' };
  }

  const {
    nowMs = Date.now(),
    committedTtlMs = 3 * 24 * 60 * 60 * 1000,
    failedTtlMs = 7 * 24 * 60 * 60 * 1000,
    pendingTtlMs = 30 * 60 * 1000
  } = options;

  const journal = readJournal(businessId);
  const out = [];
  let removedCommitted = 0;
  let removedFailed = 0;
  let stalePendingMarkedFailed = 0;

  for (const entry of journal) {
    if (!entry) continue;
    const ts =
      Date.parse(entry.updatedAt || entry.createdAt || '') ||
      Date.parse(entry.createdAt || '') ||
      0;
    const ageMs = ts > 0 ? Math.max(0, nowMs - ts) : 0;

    if (entry.status === 'committed' && ageMs > committedTtlMs) {
      removedCommitted += 1;
      continue;
    }
    if (entry.status === 'failed' && ageMs > failedTtlMs) {
      removedFailed += 1;
      continue;
    }
    if (entry.status === 'pending' && ageMs > pendingTtlMs) {
      stalePendingMarkedFailed += 1;
      out.push({
        ...entry,
        status: 'failed',
        updatedAt: new Date(nowMs).toISOString(),
        error: entry.error || 'stale_pending_timeout'
      });
      continue;
    }
    out.push(entry);
  }

  writeJournal(businessId, out);
  return {
    swept: true,
    businessId: String(businessId),
    beforeCount: journal.length,
    afterCount: out.length,
    removedCommitted,
    removedFailed,
    stalePendingMarkedFailed
  };
}
